import classNames from "classnames"
import { useRouter } from "next/router"
import React, { useRef } from "react"
import { FreeMode } from "swiper/modules"
import type { SwiperRef } from "swiper/react"
import { Swiper, SwiperSlide } from "swiper/react"

import { Analytics } from "@bounce/analytics"
import { useTranslation } from "@bounce/i18n"

import { PoiChipNavItem } from "./PoiChipNavItem"
import { InteractionEvents } from "@/libs/analytics/events"
import type { RelatedPoi } from "@/libs/bounce/cities"
import type { PageType } from "@/types"

import LocationIcon from "@bounce/assets/icons/LocationPin.svg"

const NEAR_ME_POI_ID = "near-me"

export type PoiChipNavSliderProps = {
  className?: string
  from: PageType
  nearMeSearchUrl: string
  pois?: RelatedPoi[]
}

export const PoiChipNavSlider = ({ pois, className, nearMeSearchUrl, from }: PoiChipNavSliderProps) => {
  const { t } = useTranslation()
  const swiperRef = useRef<SwiperRef | null>(null)
  const router = useRouter()

  const onClickPoi = (poiId: string, href: string) => {
    Analytics.track(InteractionEvents.PoiChipPressed, { from, poiId })
    void router.push(href)
  }

  const activePoisWithUrls = pois ? pois.filter(poi => poi.active).map(poi => ({ ...poi, href: poi.url })) : []

  if (!pois || pois.length === 0) {
    return null
  }

  return (
    <div className={classNames("w-full py-2 text-black", className)}>
      <div className="relative mx-auto flex w-full max-w-screen-xl items-center md:px-6">
        <div className="pointer-events-none absolute inset-y-0 right-6 z-2 hidden w-12 bg-gradient-to-l from-primary-50 to-transparent md:block" />
        <nav className="w-full">
          <Swiper
            modules={[FreeMode]}
            ref={swiperRef}
            slidesPerView="auto"
            spaceBetween={8}
            slidesOffsetBefore={0}
            slidesOffsetAfter={0}
            freeMode={{
              enabled: true,
            }}
            className="w-full"
            tag="ul">
            <SwiperSlide key={NEAR_ME_POI_ID} className="!w-auto pl-4 md:pl-0" tag="li">
              <PoiChipNavItem
                label={t("cmp.poiChipNavSlider.nearMe", "Near me")}
                onClick={() => onClickPoi(NEAR_ME_POI_ID, nearMeSearchUrl)}
                icon={LocationIcon}
              />
            </SwiperSlide>

            {activePoisWithUrls.map(poi => (
              <SwiperSlide key={poi.id} className="!w-auto" tag="li">
                <PoiChipNavItem
                  label={poi.translatedName}
                  href={poi.href}
                  onClick={() => onClickPoi(poi.id, poi.href)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </nav>
      </div>
    </div>
  )
}
